import useApiClient from "../../useApiClient";
import { useSelector } from "react-redux";
import { useCallback } from "react";
import { withNetworkErrorHandling } from "../../helpers/withNetworkErrorHandling";

/**
 * API client hook for day plan operations.
 * Provides methods to get, save, and delete day plans.
 */
const useDayPlanAPIClient = () => {
  const { get, put, remove } = useApiClient();
  const { activeUser } = useSelector((state) => state.user);

  /**
   * Get a day plan for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<Object|null>} The day plan or null if not found
   */
  const getDayPlan = useCallback(
    async (date) => {
      if (!activeUser?.userId) {
        return null;
      }
      return await withNetworkErrorHandling(() =>
        get(`users/${activeUser.userId}/dayPlans/${date}`),
      );
    },
    [activeUser, get],
  );

  /**
   * Get day plans for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of day plans
   */
  const getDayPlansInRange = useCallback(
    async (startDate, endDate) => {
      if (!activeUser?.userId) {
        return [];
      }
      const result = await withNetworkErrorHandling(() =>
        get(
          `users/${activeUser.userId}/dayPlans?startDate=${startDate}&endDate=${endDate}`,
        ),
      );
      return result || [];
    },
    [activeUser, get],
  );

  /**
   * Save or update a day plan
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {Object} planData - The day plan data to save
   * @returns {Promise<Object|null>} The saved day plan or null on error
   */
  const saveDayPlan = useCallback(
    async (date, planData) => {
      if (!activeUser?.userId) {
        return null;
      }
      return await withNetworkErrorHandling(() =>
        put(`users/${activeUser.userId}/dayPlans/${date}`, planData),
      );
    },
    [activeUser, put],
  );

  /**
   * Delete a day plan
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<boolean>} True if deleted successfully
   */
  const deleteDayPlan = useCallback(
    async (date) => {
      if (!activeUser?.userId) {
        return false;
      }
      const result = await withNetworkErrorHandling(() =>
        remove(`users/${activeUser.userId}/dayPlans/${date}`),
      );
      return result !== null;
    },
    [activeUser, remove],
  );

  return {
    getDayPlan,
    getDayPlansInRange,
    saveDayPlan,
    deleteDayPlan,
  };
};

export default useDayPlanAPIClient;
